/**
 * Spoken turn instructions via the Web Speech API, with music ducking.
 *
 * Ducking: when Spotify is playing through THIS app (Web Playback SDK), we
 * lower its volume during the announcement and restore it after. When Spotify
 * plays elsewhere (phone app / car unit), we duck via the Web API volume call
 * where the device supports it, and just speak over the top where it doesn't.
 */

import { store } from '../core/store';
import { getLang } from '../core/i18n';

export interface Ducker {
  duck(): Promise<void>;
  restore(): Promise<void>;
}

let ducker: Ducker | null = null;
export function setDucker(d: Ducker | null): void {
  ducker = d;
}

let voice: SpeechSynthesisVoice | null = null;
let voicesLoaded = false;

function pickVoice(): void {
  const all = speechSynthesis.getVoices();
  if (!all.length) return;
  voicesLoaded = true;
  // Match the app language (Thai instructions need a Thai voice); prefer the
  // natural/neural voices that Google/Microsoft ship on Android.
  const wanted = getLang() === 'th' ? 'th' : 'en';
  const match = all.filter((v) => v.lang.toLowerCase().startsWith(wanted));
  voice =
    match.find((v) => /natural|neural|premium/i.test(v.name)) ??
    match[0] ??
    all.find((v) => v.lang.startsWith('en')) ??
    all[0];
}

/** Re-pick after a language switch. */
export function refreshVoice(): void {
  pickVoice();
}

if ('speechSynthesis' in window) {
  pickVoice();
  speechSynthesis.addEventListener('voiceschanged', pickVoice);
}

let speaking = false;
const queue: string[] = [];

function speakNext(): void {
  const text = queue.shift();
  if (text === undefined) return;

  const s = store.get().settings;
  const u = new SpeechSynthesisUtterance(text);
  if (!voicesLoaded) pickVoice();
  if (voice) u.voice = voice;
  u.volume = s.voiceVolume;
  u.rate = 1.04;

  speaking = true;
  const finish = async () => {
    speaking = false;
    if (queue.length) {
      speakNext();
    } else if (s.duckMusic) {
      await ducker?.restore().catch(() => {});
    }
  };
  u.onend = finish;
  u.onerror = finish;

  const start = () => speechSynthesis.speak(u);
  if (s.duckMusic && ducker) {
    ducker
      .duck()
      .catch(() => {})
      .finally(start);
  } else {
    start();
  }
}

/** Queue an announcement. Coalesces: a newer instruction replaces queued ones. */
export function announce(text: string): void {
  if (!('speechSynthesis' in window)) return;
  if (!store.get().settings.voiceEnabled) return;

  queue.length = 0; // stale queued instructions are worse than silence
  queue.push(text);
  if (!speaking) speakNext();
}

export function stopSpeaking(): void {
  queue.length = 0;
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  speaking = false;
}

/** iOS/Android unlock: call once from a user gesture so later speech works. */
export function primeSpeech(): void {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(' ');
  u.volume = 0;
  speechSynthesis.speak(u);
}
